import React, {createContext, useContext, useEffect, useState} from "react";
import {configureApiBaseUrl} from "../api/base";

const RuntimeConfigContext = createContext(null);
const RUNTIME_CONFIG_KEYS = [
  "PUBLIC_ALPHASEARCH_API_BASE_URL",
  "PUBLIC_GOOGLE_MAPS_JAVASCRIPT_API_KEY",
  "PUBLIC_SIGNUP_ENABLED",
  "PUBLIC_VERSION",
];

function validateRuntimeConfig(config) {
  const keys = config && typeof config === "object"
    ? Object.keys(config).filter((key) => key !== "PUBLIC_GA4_MEASUREMENT_ID").sort()
    : [];
  const expectedKeys = [...RUNTIME_CONFIG_KEYS].sort();
  if (JSON.stringify(keys) !== JSON.stringify(expectedKeys)) {
    throw new Error("Runtime config keys do not match the public allowlist");
  }
  if (
    typeof config.PUBLIC_ALPHASEARCH_API_BASE_URL !== "string"
    || !config.PUBLIC_ALPHASEARCH_API_BASE_URL
    || typeof config.PUBLIC_GOOGLE_MAPS_JAVASCRIPT_API_KEY !== "string"
    || typeof config.PUBLIC_SIGNUP_ENABLED !== "string"
    || typeof config.PUBLIC_VERSION !== "string"
  ) {
    throw new Error("Runtime config values are invalid");
  }
  const apiUrl = new URL(config.PUBLIC_ALPHASEARCH_API_BASE_URL);
  if (!["http:", "https:"].includes(apiUrl.protocol)) {
    throw new Error("Runtime API URL must use HTTP or HTTPS");
  }
  return {
    ...config,
    PUBLIC_GA4_MEASUREMENT_ID: typeof config.PUBLIC_GA4_MEASUREMENT_ID === "string"
      ? config.PUBLIC_GA4_MEASUREMENT_ID : "",
  };
}

export function useRuntimeConfig() {
  const config = useContext(RuntimeConfigContext);
  if (!config) {
    throw new Error("Runtime config is not available");
  }
  return config;
}

export default function RuntimeConfigProvider({children}) {
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    fetch("/config.json", {
      cache: "no-store",
      headers: {Accept: "application/json"},
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Runtime config request failed with ${response.status}`);
        }
        return response.json();
      })
      .then((runtimeConfig) => {
        if (active) {
          const validatedConfig = validateRuntimeConfig(runtimeConfig);
          configureApiBaseUrl(validatedConfig.PUBLIC_ALPHASEARCH_API_BASE_URL);
          setConfig(validatedConfig);
        }
      })
      .catch(() => {
        if (active) {
          setError(true);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  if (error) {
    return (
      <main role="alert">
        <h1>AlphaSearch could not start</h1>
        <p>Runtime configuration could not be loaded. Refresh the page to try again.</p>
      </main>
    );
  }

  if (!config) {
    return <div role="status">Loading AlphaSearch configuration…</div>;
  }

  return (
    <RuntimeConfigContext.Provider value={config}>
      {children}
    </RuntimeConfigContext.Provider>
  );
}
